// **The demo module's stacking order, in one place** (`pvp-ui-fixes` W4).
//
// Every surface here used to declare its own `zIndex:` literal — twelve of them across nine files,
// with nothing relating one to another. The only way to know whether a new overlay would land
// above or below an existing one was to grep for numbers and compare by eye, which is how the
// projector acquired a view toolbar painting over the prompt aside beside it.
//
// **Renamed, not renumbered.** Every value below is exactly what shipped; `z-layers.test.ts` pins
// them. Changing what paints over what is a behaviour change and should arrive as one, not as a
// side effect of tidying.
//
// Two groups, because they answer different questions. Folding them into one ramp would imply
// `slideContent: 2` and `playoffLink: 4` are comparable, when the first only ever competes with
// its own siblings inside a slide's stacking context and would keep working at any value.

/**
 * Stacking **within one component's own context** — not positions in the global scale.
 *
 * A value here is meaningful only against the other children of the same parent.
 */
export const Z_LOCAL = {
  /** The decorative festival element behind the cover's content (`DemoApp`'s cover). */
  coverElement: 1,
  /** `SlideFrame`'s fit-scaler and its animated child, above the frame's background art. */
  slideContent: 2,
} as const;

/**
 * The demo module's **global** layer scale. Higher paints later.
 *
 * Two pairs deliberately share a value (`chapterSurface`/`modalScrim`, `hostRebind`/
 * `presenterToolbar`). They are kept as separate names rather than collapsed because they are
 * different surfaces that merely coincide today — one name for both would make a later divergence
 * look like a renumbering of something that already existed.
 */
export const Z = {
  /** The tournament chapters' playoff link: above the stage, below the HUD. */
  playoffLink: 4,
  /** `DemoHud`'s top-right chapter controls. */
  hudControls: 5,
  /**
   * The bottom prompt bar and the HUD's title band — **one deliberately shared layer**. The
   * reason is measured rather than aesthetic: the prompt bar accumulates a failure/reply row, and
   * the code-edit box above it reserves a fixed gap for the tallest state. See the note in
   * `DemoApp.tsx` where that clearance is chosen.
   */
  hudBar: 6,
  /** A full-screen chapter surface that replaces the stage (live-match setup, waiting, result). */
  chapterSurface: 20,
  /** The API modal's scrim. */
  modalScrim: 20,
  /** The cover's exit animation, above the chapter surfaces it uncovers. */
  coverExit: 30,
  /** The rate-limit toast. */
  toast: 40,
  /** The stage-failure band: a band, not a modal — the show still advances under it. */
  stageFailure: 99,
  /** The host-rebind prompt, inside the fullscreen element so it is above every scene. */
  hostRebind: 100,
  /** The presenter's Field/Red/Blue view toolbar. */
  presenterToolbar: 100,
  /** Presenter Preview's read-only label. */
  previewLabel: 110,
  /** Presenter Preview's refusal notice — the topmost surface in the module. */
  previewNotice: 120,
} as const;
